/**
 * Checkout Confirmation Page
 * Shows success message and redirects to dashboard with new entitlements
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Sparkles, Mail } from 'lucide-react';
import { useEntitlements } from '../contexts/EntitlementContext';
import type { SubscriptionTier } from '../types/subscription';

export function CheckoutConfirmationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessLevel, refreshEntitlements } = useEntitlements();
  
  const plan = searchParams.get('plan') as SubscriptionTier || 'free';
  const cycle = searchParams.get('cycle') || 'monthly';
  const isContact = searchParams.get('contact') === 'true';
  const returnUrl = searchParams.get('returnUrl') || '/projects';
  const [countdown, setCountdown] = useState(5);
  
  // Refresh entitlements on mount
  useEffect(() => {
    refreshEntitlements();
  }, [refreshEntitlements]);
  
  // Countdown and redirect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      navigate(returnUrl);
    }
  }, [countdown, navigate, returnUrl]);
  
  const getPlanName = (tier: SubscriptionTier) => {
    const names: Record<SubscriptionTier, string> = {
      free: 'Starter',
      pro: 'Professional',
      business: 'Business',
      enterprise: 'Enterprise'
    };
    return names[tier];
  };
  
  const getFeaturesByPlan = (tier: SubscriptionTier) => {
    const features: Record<SubscriptionTier, string[]> = {
      free: [
        'Basic loop load calculator',
        'Device database access',
        'Watermarked PDF exports'
      ],
      pro: [
        'NFPA references & compliance warnings',
        'Battery calculations',
        'Voltage drop analysis',
        'Unlimited PDF exports',
        'Email support (48h response)'
      ],
      business: [
        'All Professional features',
        'Panel load summaries',
        'Inspection checklists',
        'Team collaboration (5 members)',
        'Priority support (24h response)',
        'Custom branding'
      ],
      enterprise: [
        'All Business features',
        'Custom device types',
        'Custom project templates',
        'API access',
        'SSO integration',
        'Dedicated account manager',
        '24/7 support'
      ]
    };
    return features[tier];
  };
  
  // Enterprise contact confirmation
  if (isContact) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-12 h-12 text-purple-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              Thank You for Your Interest!
            </h1>
            
            <p className="text-lg text-slate-600 mb-8">
              Our sales team will contact you within 24 hours to discuss your enterprise needs and create a custom solution for your organization.
            </p>
            
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
              <ul className="text-left space-y-2 text-slate-600">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>A dedicated account manager will reach out</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>We'll discuss your specific requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Receive a custom quote and implementation plan</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Schedule a demo and onboarding session</span>
                </li>
              </ul>
            </div>
            
            <div className="text-sm text-slate-500 mb-6">
              Redirecting to dashboard in {countdown} seconds...
            </div>
            
            <button
              onClick={() => navigate(returnUrl)}
              className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
            >
              Go to Dashboard Now
              <ArrowRight className="w-5 h-5 inline ml-2" />
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Standard plan confirmation
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-xl shadow-2xl p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto shadow-lg">
              <CheckCircle2 className="w-16 h-16 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
              <Sparkles className="w-6 h-6 text-yellow-900" />
            </div>
          </div>
          
          {/* Success Message */}
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            {plan === 'free' ? 'Welcome Aboard!' : 'Payment Successful!'}
          </h1>
          
          <p className="text-xl text-slate-600 mb-8">
            {plan === 'free' 
              ? `You're now on the ${getPlanName(plan)} plan`
              : `Your ${getPlanName(plan)} subscription is now active`
            }
          </p>
          
          {/* Plan Details */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-indigo-900">{getPlanName(plan)}</h2>
                {plan !== 'free' && (
                  <p className="text-indigo-600 font-medium">
                    {cycle === 'monthly' ? 'Monthly' : 'Annual'} Billing
                  </p>
                )}
              </div>
            </div>
            
            <div className="text-left">
              <h3 className="font-semibold text-slate-900 mb-3">Your new features:</h3>
              <ul className="space-y-2">
                {getFeaturesByPlan(plan).map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Current Access Level */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-slate-600">
              Current Access Level: <span className="font-bold text-indigo-600 uppercase">{accessLevel}</span>
            </p>
          </div>
          
          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-slate-900 mb-3">What's next?</h3>
            <ul className="text-left space-y-2 text-slate-700">
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>All features are now unlocked in your dashboard</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Start creating projects with advanced tools</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Access NFPA references and compliance warnings</span>
              </li>
              {plan !== 'free' && (
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Check your email for receipt and account details</span>
                </li>
              )}
            </ul>
          </div>
          
          {/* Redirect Info */}
          <div className="text-sm text-slate-500 mb-6">
            Redirecting to your dashboard in {countdown} seconds...
          </div>
          
          {/* Action Button */}
          <button
            onClick={() => navigate(returnUrl)}
            className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg"
          >
            Go to Dashboard Now
            <ArrowRight className="w-5 h-5 inline ml-2" />
          </button>
          
          {/* Support */}
          <p className="text-sm text-slate-500 mt-8">
            Need help? Contact support at{' '}
            <a href="mailto:support@example.com" className="text-indigo-600 hover:text-indigo-700 font-medium">
              support@example.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
